import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { useProfileRoutesConditions } from '@pages/Profile/ProfileRoutes'
import { EconomicProfilePage } from '@pages/Signup/Pages/EconomicProfile/EconomicProfile.page'
import { FinancialInfoPage } from '@pages/Signup/Pages/FinancialInfo/FinancialInfo.page'
import { FinancialInfoFailPage } from '@pages/Signup/Pages/FinancialInfo/FinancialInfoFail.page'
import { FinancialInfoSuccessPage } from '@pages/Signup/Pages/FinancialInfo/FinancialInfoSuccess.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useEntitySettings } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSignup } from '@context/SignupContext'

import { isDepositBeforeKYC, isSumsubNeeded } from '@utils/AccountAccess/accountKYCStatuses'
import { showApptestRiskScreen } from '@utils/AccountAccess/accountProductStatuses'
import { isTickmillPartnerType } from '@utils/companyName.utils'

import { isVerificationExpedited } from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'

import { AccountTypeStep } from '../Pages/AccountType/AccountType'
import { ConfirmPage } from '../Pages/ConfirmPage/ConfirmPage'
import { DocumentsInfoPage } from '../Pages/DocumentsInfo/DocumentsInfoPage'
import { AppTestRiskAcceptancePage } from '../Pages/FinancialInfo/AppTestRiskAcceptancePage'
import { FirstTimeDepositOrDocumentsResolver } from '../Pages/FirstTimeDeposit/FirstTimeDepositOrDocumentsResolver'
import { SumsubSignupPage } from '../Pages/Sumsub/SumsubSignupPage'
import { useCompanyDetailsRoutes } from './useCompanyDetailsRoutes'
import { usePersonalDetailsRoutes } from './usePersonalDetailsRoutes'

const NavigateWithSearch: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} />
}

export const useSignupRoutes = (): RouteConfig[] => {
  const location = useLocation()
  const profileRoutesVisibility = useProfileRoutesConditions()
  const { product } = useProductReadContext()
  const { account } = useAccountReadContext()
  const personalDetailsRoutes = usePersonalDetailsRoutes()
  const companyDetailsRoutes = useCompanyDetailsRoutes()
  const { signupData } = useSignup()
  const [entity, setEntity] = useEntitySettings()

  useEffect(() => {
    if (signupData.lead) {
      if (signupData.lead.tickmillCompany.id) {
        if (entity.entity !== signupData.lead.tickmillCompany.id) {
          setEntity({ entity: signupData.lead.tickmillCompany.id })
        }
      }
    }
  }, [entity.entity, setEntity, signupData.lead])

  const shouldAllowActivatedOnboardingPath =
    isAccountActivatedStatus(account) &&
    (isDepositBeforeKYC(account) || isVerificationExpedited(account)) &&
    ['/sign-up/financial-info/', '/sign-up/economic-profile/', '/sign-up/first-time-deposit'].some(
      (path) => location.pathname.includes(path)
    )

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'sign-up',
        condition:
          !isTickmillPartnerType(entity.entity) &&
          (!isAccountActivatedStatus(account) || shouldAllowActivatedOnboardingPath),
        element: (
          <NavigateWithSearch
            to={
              account?.id
                ? profileRoutesVisibility.isEconomicProfileVisible
                  ? `/sign-up/economic-profile/${account.id}`
                  : `/sign-up/financial-info/${account.id}`
                : '/sign-up/account-type'
            }
          />
        ),
        children: [
          { path: 'first-time-deposit', element: <FirstTimeDepositOrDocumentsResolver /> },
          {
            path: 'account-type',
            element: <AccountTypeStep />,
          },
          ...['financial-documents-info/*', 'documents-info/*', 'verification/*'].map((path) => ({
            path,
            element: <Navigate to='/dashboard/traders-room' />,
          })),
          ...personalDetailsRoutes,
          ...companyDetailsRoutes,
          {
            path: 'financial-info/:clientId',
            element: <FinancialInfoPage />,
            children: [
              {
                path: 'risk',
                condition: showApptestRiskScreen(account, product),
                element: <AppTestRiskAcceptancePage />,
              },
              {
                path: 'complete',
                element: <FinancialInfoSuccessPage />,
              },
              { path: 'fail/:failReason', element: <FinancialInfoFailPage /> },
            ],
          },
          {
            path: 'economic-profile/:clientId',
            element: <EconomicProfilePage />,
            condition: profileRoutesVisibility.isEconomicProfileVisible,
            children: [
              {
                path: 'complete',
                element: <FinancialInfoSuccessPage />,
              },
              { path: 'fail', element: <FinancialInfoFailPage /> },
            ],
          },
          {
            path: 'documents-info/:clientId',
            element: <DocumentsInfoPage />,
            condition: !isSumsubNeeded(account),
          },
          {
            path: 'verification',
            element: <SumsubSignupPage />,
            condition: isSumsubNeeded(account),
          },
          {
            path: 'confirm',
            element: <ConfirmPage />,
          },
          {
            path: '*',
            element: (
              <NavigateWithSearch
                to={
                  account?.id
                    ? profileRoutesVisibility.isEconomicProfileVisible
                      ? `/sign-up/economic-profile/${account.id}`
                      : `/sign-up/financial-info/${account.id}`
                    : '/sign-up/personal-details/basic'
                }
              />
            ),
          },
        ],
      },
    ],
    [
      entity.entity,
      account,
      profileRoutesVisibility.isEconomicProfileVisible,
      personalDetailsRoutes,
      companyDetailsRoutes,
      product,
      shouldAllowActivatedOnboardingPath,
    ]
  )
}
